import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import PageBreadcrumb from "@/components/base/PageBreadcrumb";
import EmptyState from "@/components/base/EmptyState";
import DetailActions from "@/components/feature/DetailActions";
import { articles } from "@/mocks/articles";
import usePageMeta from "@/hooks/usePageMeta";
import { formatDate, formatViews, getRelatedArticles } from "@/utils/content";

export default function ArticleDetailPage() {
  const { id } = useParams();
  const article = articles.find((item) => item.id === id);

  usePageMeta(
    article ? `${article.title} - 挠脚心网站` : "文章不存在 - 挠脚心网站",
    article
      ? article.excerpt
      : "该文章不存在或已被删除，返回挠脚心网站首页继续浏览其他内容。",
    article ? `${article.tags.join(",")},趣味文章,挠脚心网站` : "趣味文章,挠脚心网站"
  );

  if (!article) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="mx-auto flex w-full max-w-[1240px] flex-1 flex-col justify-center px-4 py-16 md:px-6">
          <EmptyState
            icon="ri-file-warning-line"
            title="该文章不存在或已被删除"
            description="可能链接里的编号有误，也可能这篇内容已经下线。回到文章列表挑一篇读读吧。"
            actionLabel="返回文章列表"
            actionTo="/articles"
          />
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = getRelatedArticles(article, articles);

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <div className="mx-auto w-full max-w-[1240px] px-4 pb-10 pt-6 md:px-6 md:pt-8">
          <PageBreadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "趣味文章", to: "/articles" },
              { label: article.title },
            ]}
          />

          <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-12">
            <article className="lg:col-span-8">
              <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                <div className="relative aspect-[16/9] w-full bg-background-200">
                  <img
                    src={article.cover}
                    alt={`${article.title}的文章配图`}
                    title={`${article.title} - 趣味文章`}
                    className="h-full w-full object-cover object-top"
                  />
                  <span className="absolute left-4 top-4 rounded-md bg-accent-500 px-2.5 py-1 text-[11px] font-medium text-background-50">
                    {article.tag}
                  </span>
                </div>

                <div className="p-5 md:p-8">
                  <h1 className="font-heading text-2xl leading-snug text-foreground-950 md:text-3xl">
                    {article.title}
                  </h1>
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500 md:text-sm">
                    <span className="flex items-center gap-1.5 whitespace-nowrap">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent-100 text-[11px] text-accent-800">
                        {article.author.slice(0, 1)}
                      </span>
                      {article.author}
                    </span>
                    <span className="whitespace-nowrap">发布于 {formatDate(article.date)}</span>
                    <span className="whitespace-nowrap">{article.readMinutes} 分钟阅读</span>
                    <span className="flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-eye-line"></i>
                      {formatViews(article.views)}
                    </span>
                  </div>

                  <p className="mt-5 rounded-md bg-background-100 px-4 py-3 text-sm leading-relaxed text-foreground-700">
                    {article.excerpt}
                  </p>

                  <div className="mt-6">
                    {article.content.map((block, index) => {
                      if (block.type === "h2") {
                        return (
                          <h2
                            key={index}
                            className="mt-8 flex items-center gap-2 font-heading text-lg text-foreground-950 md:text-xl"
                          >
                            <span className="h-4 w-1 rounded-full bg-primary-500"></span>
                            {block.text}
                          </h2>
                        );
                      }
                      if (block.type === "img") {
                        return (
                          <figure key={index} className="my-6">
                            <div className="aspect-[16/9] w-full overflow-hidden rounded-lg bg-background-200">
                              <img
                                src={block.src}
                                alt={block.caption}
                                title={`${article.title} - 配图`}
                                className="h-full w-full object-cover object-top"
                              />
                            </div>
                            <figcaption className="mt-2 text-center text-xs text-foreground-500">
                              {block.caption}
                            </figcaption>
                          </figure>
                        );
                      }
                      if (block.type === "quote") {
                        return (
                          <blockquote
                            key={index}
                            className="my-6 rounded-lg border-l-4 border-primary-500 bg-primary-50 px-5 py-4 text-sm font-medium leading-relaxed text-foreground-800 md:text-base"
                          >
                            {block.text}
                          </blockquote>
                        );
                      }
                      return (
                        <p
                          key={index}
                          className="mt-4 text-sm leading-loose text-foreground-700 md:text-base"
                        >
                          {block.text}
                        </p>
                      );
                    })}
                  </div>

                  <div className="mt-7 flex flex-wrap gap-2 border-t border-background-100 pt-5">
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>

                  <div className="mt-5">
                    <DetailActions
                      favoriteKey={`article:${article.id}`}
                      title={article.title}
                      extra="收藏与分享均为演示功能，收藏记录只保存在本机浏览器"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-4 flex items-start gap-2 rounded-md bg-background-100 px-4 py-3 text-xs leading-relaxed text-foreground-600">
                <i className="ri-information-line mt-0.5 text-base text-secondary-600"></i>
                本文为虚构演示内容，仅用于展示站点排版与阅读体验，不构成任何专业建议。
              </div>
            </article>

            <aside className="lg:col-span-4">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="flex items-center gap-2 font-heading text-base text-foreground-950">
                    <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                      <i className="ri-book-open-line text-sm"></i>
                    </span>
                    相关阅读
                  </h2>
                  <span className="text-[11px] text-foreground-400">同标签优先</span>
                </div>
                <div className="flex flex-col gap-3">
                  {related.map((item) => (
                    <Link
                      key={item.id}
                      to={`/articles/detail/${item.id}`}
                      className="group flex gap-3 rounded-md p-1 transition-colors hover:bg-background-100"
                    >
                      <div className="h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-200">
                        <img
                          src={item.cover}
                          alt={`${item.title}的配图缩略图`}
                          title={`${item.title} - 趣味文章`}
                          className="h-full w-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="line-clamp-2-custom text-sm leading-snug text-foreground-900 transition-colors group-hover:text-accent-700">
                          {item.title}
                        </p>
                        <p className="mt-1.5 text-[11px] text-foreground-500">
                          {item.tag} · {item.readMinutes} 分钟
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
                <Link
                  to="/articles"
                  className="mt-4 flex items-center justify-center gap-1 whitespace-nowrap rounded-md border border-background-300 py-2.5 text-xs text-foreground-700 transition-colors hover:bg-background-100"
                >
                  浏览全部文章
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <div className="mt-4 rounded-lg border border-primary-200 bg-primary-50 p-5">
                <p className="flex items-center gap-2 text-sm font-medium text-foreground-900">
                  <i className="ri-chat-smile-2-line text-base text-primary-700"></i>
                  想聊聊这篇？
                </p>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  社区里有和这篇文章相关的话题讨论，欢迎去看看大家怎么说。
                </p>
                <Link
                  to="/community"
                  className="mt-3 flex w-fit items-center gap-1 whitespace-nowrap rounded-md bg-foreground-900 px-3 py-2 text-xs text-background-50 transition-colors hover:bg-foreground-800"
                >
                  去社区
                  <i className="ri-arrow-right-line text-sm"></i>
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}